/**
 * The AppFlow is a router that is responsible for the general flow of the application
 * The AppFlow determines the following:
 *
 * - The order of the pages the user navigates through
 * - The current page navigation stepper
 * - The next page to navigate to
 *
 * The AppFlow is paired together with the FlowPage component. All
 * routes declared through the FlowPage component are a part of the app flow.
 * Other routes such as error pages can omitted from the app flow
 * with the prop ignoreFromAppFlow added to the FlowPage component
 */
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { Route, Routes } from "react-router-dom";

import { appFlowAtom, flowWithProgressStepperAtom } from "atoms/appFlow";

import { RemoveTrailingSlash } from "./RemoveTrailingSlash";
import { getFlowPages } from "./getFlowPages";
import { getProgressStepperRoutes } from "./getProgressStepRoutes";
import { AppFlowJSX } from "./types";

type AppFlowProps = {
  children: AppFlowJSX;
};

export const AppFlow = ({ children }: AppFlowProps) => {
  const setFlowWithStepper = useSetAtom(flowWithProgressStepperAtom);
  const setAppFlow = useSetAtom(appFlowAtom);

  const flowPages = getFlowPages(children);

  const appRoutes = flowPages
    .filter((node) => !node.props?.ignoreFromAppFlow)
    .map((node) => ({
      path: node.props.path,
      checkpoint: node.props.checkpoint,
    }));

  const progressStepperRoutes = getProgressStepperRoutes(flowPages);

  useEffect(() => {
    setAppFlow(appRoutes);
    setFlowWithStepper(progressStepperRoutes);
  }, [progressStepperRoutes, appRoutes, setAppFlow, setFlowWithStepper]);

  return (
    <>
      <RemoveTrailingSlash />
      <Routes>
        {flowPages.map((child) => (
          <Route key={child.props.path} path={child.props.path} element={child} />
        ))}
      </Routes>
    </>
  );
};
